import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { DashboardService } from '../../services/dashboard.service';
import {
  dashboardInfo,
  dashboardInfoSuccess,
  dashboardInfoFailure,
  services,
  servicesSuccess,
  servicesFailure,
  storeService,
  storeServiceSuccess,
  storeServiceFailure,
  uploadCSVSuccess,
  uploadCSVFailure,
  uploadCSV, uploadEmails, uploadEmailsSuccess, uploadEmailsFailure,
} from './dashboard.actions';

@Injectable()
export class DashboardEffects {
  dashboardInfo$ = createEffect(() =>
    this.actions$.pipe(
      // Listen for the `refineIcpScore` action
      ofType(dashboardInfo),
      // tap(() => console.log('Dashboard info effect triggered')),
      // Call the `refineIcpScore` service method when the action is dispatched
      switchMap(() =>
        this.dashboardService.getDashboardInfo().pipe(
          // Use the service properly
          // tap(() => console.log('Calling dashboard info service')),
          // On success, dispatch the `refineIcpScoreSuccess` action
          map((response) => {
            if (!response || !response.data) {
              // If the response is not structured as expected, throw an error
              throw new Error('Invalid dashboard info score response');
            }
            // console.log('Dashboard info successful', response.data);

            return dashboardInfoSuccess({ result: response.data });
          }),
          // On failure, dispatch the `refineIcpScoreFailure` action
          catchError((error) => {
            console.log('Dashboard info failed', error);
            return of(dashboardInfoFailure({ error }));
          })
        )
      )
    )
  );

  services$ = createEffect(() =>
    this.actions$.pipe(
      // Listen for the `refineIcpScore` action
      ofType(services),
      // tap(() => console.log('Services effect triggered')),
      // Call the `refineIcpScore` service method when the action is dispatched
      switchMap(() =>
        this.dashboardService.getServicesList().pipe(
          // Use the service properly
          // tap(() => console.log('Calling services')),
          // On success, dispatch the `refineIcpScoreSuccess` action
          map((response) => {
            if (!response || !response.data) {
              // If the response is not structured as expected, throw an error
              throw new Error('Invalid dashboard info score response');
            }

            //console.log('Services successful', response.data);

            return servicesSuccess({ result: response.data });
          }),
          // On failure, dispatch the `refineIcpScoreFailure` action
          catchError((error) => {
            console.log('Dashboard info failed', error);
            return of(servicesFailure({ error }));
          })
        )
      )
    )
  );

  storeService = createEffect(() =>
    this.actions$.pipe(
      ofType(storeService),
      switchMap(({ serviceId, message }) =>
        this.dashboardService.storeService(serviceId, message).pipe(
          map((response) => {
            if (!response || !response.data) {
              throw new Error('Invalid store service response');
            }
            return storeServiceSuccess({ result: response.data });
          }),
          catchError((error) => of(storeServiceFailure({ error })))
        )
      )
    )
  );

  // CSV Upload effect
  uploadCSV$ = createEffect(() =>
    this.actions$.pipe(
      ofType(uploadCSV),
      switchMap(({ file, batchType }) =>
        this.dashboardService.uploadCSV(file, batchType).pipe(
          map((response) => {
            if (!response) {
              throw new Error('Invalid upload CSV response');
            }

            return uploadCSVSuccess({ result: response.data });
          }),
          catchError((error) => of(uploadCSVFailure({ error })))
        )
      )
    )
  );

  // Email Upload effect
  uploadEmails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(uploadEmails),
      tap(action => console.log('[DashboardEffects] uploadEmails action dispatched:', action)),
      switchMap(({ emails }) =>
        this.dashboardService.uploadEmails(emails).pipe(
          tap(response => console.log('[DashboardEffects] uploadEmails response:', response)),
          map((response) => {
            if (!response) {
              console.error('[DashboardEffects] Empty response from uploadEmails');
              throw new Error('Empty response from email upload');
            }

            // More lenient check for success
            if (response.data && typeof response.data === 'object') {
              return uploadEmailsSuccess({ result: response.data });
            } else {
              return uploadEmailsSuccess({ result: {
                  success: true,
                  message: 'Emails uploaded successfully',
                  count: emails.length
                }});
            }
          }),
          catchError((error) => {
            console.error('[DashboardEffects] Error in uploadEmails effect:', error);
            return of(uploadEmailsFailure({
              error: error.message ? { message: error.message } : error
            }));
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private dashboardService: DashboardService
  ) {}
}
